import React from 'react';
import { Checkbox, FormField } from '@parsina/framework';
import { C } from '../../../constants/theme';
import { useTranslation } from '../../../i18n';
import ChannelIcon from '../../../components/common/ChannelIcon';
import { CHANNELS } from '../../../lib/messaging';
import {
  NOTIFY_CHANNEL_ORDER, whatsappBlocked, withChannel,
} from '../../../lib/appointmentNotifications';
import useWhatsappSession from '../../../hooks/useWhatsappSession';

/**
 * «ارسال اعلان به بیمار» — one compact row of channel ticks at the foot of a booking.
 *
 * The caller owns the value and prefills it from «تنظیمات › اعلان‌ها»; this row only
 * renders it. One tick per channel, in the order «ارسال پیام» lists its channel cards,
 * each a real checkbox — Tab reaches it, Space flips it, and Enter still submits the
 * form, so the booking stays keyboard-only (clinic-app/CLAUDE.md §4.2).
 *
 * A channel that cannot send right now is shown disabled with the reason beside it,
 * and its tick is DROPPED rather than kept: a patient told nothing because the clinic's
 * WhatsApp was unpaired is a surprise the toast should never be the first to break.
 */
export default function NotifyPatientField({
  value, onChange, disabled = false, disabledHint = null,
}) {
  const { t } = useTranslation();
  const { session } = useWhatsappSession();
  const waBlocked = whatsappBlocked(session);
  const channels = value || [];

  React.useEffect(() => {
    if (waBlocked && channels.includes('whatsapp')) onChange(withChannel(channels, 'whatsapp', false));
    // Only when the session's verdict changes — not on every tick the desk makes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waBlocked]);

  const hint = disabled ? disabledHint : (waBlocked ? t('reception.notify.whatsappNotLinked') : null);

  return (
    <FormField label={t('reception.notify.label')}>
      <div
        role="group"
        aria-label={t('reception.notify.label')}
        data-testid="booking-notify"
        data-state={disabled ? 'disabled' : 'idle'}
        style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 18, rowGap: 8 }}
      >
        {NOTIFY_CHANNEL_ORDER.map((key) => {
          const off = disabled || (key === 'whatsapp' && waBlocked);
          return (
            <Checkbox
              key={key}
              checked={!off && channels.includes(key)}
              disabled={off}
              onChange={(next) => onChange(withChannel(channels, key, next === true))}
              data-testid={`booking-notify-${key}`}
              label={(
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <ChannelIcon channel={key} size={14} color={C.textMuted} />
                  {t(CHANNELS[key].labelKey)}
                </span>
              )}
            />
          );
        })}
      </div>
      {hint ? (
        <div data-testid="booking-notify-hint" style={{ fontSize: 11.5, color: C.textMuted, marginTop: 6, lineHeight: 1.7 }}>
          {hint}
        </div>
      ) : null}
    </FormField>
  );
}
